(function (factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else {
    const api = factory();
    const start = () => document.querySelectorAll('[data-popup-editor]').forEach(root => api.mount(root, window));
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
    else start();
  }
})(function () {
  'use strict';
  const esc = value => String(value).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const storeSlug = name => (String(name).toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'your-store') + '.example';
  const consentText = name => name ? 'Sign up for emails from ' + name + '. You can unsubscribe at any time.' : 'Sign up for our emails. You can unsubscribe at any time.';
  // Render the card alone so the makeover can measure future layouts without
  // changing fields, focus, or the saved draft.
  function paintCard(card, config, activeElement) {
    if (!card) return;
    card.querySelectorAll('[data-popup-preview-field]').forEach(el => {
      const key = el.dataset.popupPreviewField;
      if (key in config && activeElement !== el && el.textContent !== config[key]) el.textContent = config[key];
    });
    const styles = {
      '--pe-popup-bg': config.background,
      '--pe-popup-text': config.textColor,
      '--pe-popup-button': config.buttonColor,
      '--pe-popup-button-text': config.buttonTextColor,
      '--pe-popup-heading-size': config.headingSize + 'px',
      '--pe-popup-font': config.font === 'Georgia' ? 'Georgia, serif' : config.font === 'Trebuchet MS' ? '"Trebuchet MS", sans-serif' : 'Arial, sans-serif'
    };
    Object.entries(styles).forEach(([key, value]) => {
      if (card.style.getPropertyValue(key) !== value) card.style.setProperty(key, value);
    });
    if (card.dataset.layout !== config.layout) card.dataset.layout = config.layout;
    const name = card.querySelector('[data-popup-name-field]'), image = card.querySelector('[data-popup-image]');
    if (name.hidden !== !config.firstName) name.hidden = !config.firstName;
    if (image.hidden !== (config.layout !== 'split')) image.hidden = config.layout !== 'split';
    const brand = card.querySelector('[data-popup-brand]'), consent = card.querySelector('[data-popup-consent]');
    if (brand && activeElement !== brand && brand.textContent !== config.storeName) brand.textContent = config.storeName;
    if (brand && brand.hidden !== !config.storeName) brand.hidden = !config.storeName;
    if (consent && consent.textContent !== consentText(config.storeName)) consent.textContent = consentText(config.storeName);
  }
  function mount(root, view) {
    if (root.dataset.popupReady) return;
    const rules = view.MyUserPopupReview;
    if (!rules) return;
    root.dataset.popupReady = 'true';
    const doc = root.ownerDocument;
    const pageScroll = root.dataset.popupScroll === 'page';
    const homepage = root.dataset.popupDemo === 'true';
    const one = s => root.querySelector(s);
    const all = s => Array.from(root.querySelectorAll(s));
    const media = view.matchMedia ? view.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };
    const copyFields = { heading: ['Heading', 80], subheading: ['Description', 180], cta: ['Button text', 40], decline: ['Dismiss link', 60],
      storeName: ['Store name', 40], thanks: ['Thank-you message', 160] };
    const multiline = new Set(['subheading', 'thanks']);
    const inlineFields = new Set(['heading', 'subheading', 'cta', 'decline']);
    function draftText(key, value) {
      const plain = String(value).replace(/\r\n?/g, '\n').replace(/[\u0000-\u0008\u000b-\u001f\u007f]/g, '');
      const valueText = multiline.has(key) ? plain : plain.replace(/[\n\t]/g, ' ');
      let clipped = valueText.slice(0, copyFields[key][1]);
      // A browser caret uses UTF-16 offsets. Do not leave half an emoji at the limit.
      if (/[\uD800-\uDBFF]$/.test(clipped)) clipped = clipped.slice(0, -1);
      return clipped;
    }
    function restoreDraft(source) {
      const draft = rules.normalize(source);
      Object.keys(copyFields).forEach(key => {
        if (typeof source?.[key] === 'string') draft[key] = draftText(key, source[key]);
      });
      return draft;
    }
    const draftStorageKey = 'myuser:popup-example:v1';
    // Older visits saved a scripted makeover here and restored it after a
    // refresh. The makeover now plays on a copy of the sample and only becomes
    // the draft when the visitor keeps it, so there is nothing to restore.
    const staleReviewKey = 'myuser:popup-example:review:v1';
    const draftKeys = Object.keys(rules.defaults);
    const sameDraft = (a, b) => draftKeys.every(key => a[key] === b[key]);
    let config = rules.normalize(rules.defaults);
    // `dirty` is the visitor's own work. Nothing is written, and nothing claims
    // "Saved in this tab", before they change something: the notice used to
    // appear on a fresh page, over a sample nobody had touched.
    let restored = false, dirty = false, storageSaved = false, storageBlocked = false;
    let storage = null;
    try { storage = view.sessionStorage; storage.removeItem(staleReviewKey); }
    catch (_) { storage = null; storageBlocked = true; }
    if (storage) {
      try {
        const saved = storage.getItem(draftStorageKey);
        const draft = saved ? restoreDraft(JSON.parse(saved)) : null;
        // A draft identical to the sample is not "your draft".
        if (draft && !sameDraft(draft, config)) { config = draft; restored = true; dirty = true; }
      } catch (_) { /* A damaged draft falls back to the sample. */ }
    }
    let report = rules.evaluate(config);
    let selected = null;
    let mode = 'edit';
    let interaction = 'edit';
    let composing = false;
    let destroyed = false;
    let suppressStorage = false;
    let downloadURL = null;
    let reviewTargets = [];
    // One step of Undo: the draft as it was before a fix, Reset or a kept
    // makeover. Any edit of the visitor's own replaces it.
    let lastChange = null;
    // Set when a finding's "Edit …" opened the Design pane, so that pane can
    // show the finding's live status beside the field and a way back.
    let followCheck = null;
    let makeover = null;
    // While the sample makeover plays, the visitor's draft waits here and the
    // makeover edits a copy of the sample. It used to write straight into the
    // draft, so the standalone editor then opened on the robot's work.
    let stash = null;
    let confirming = false;
    let photoURL = null, photoName = '';
    const panel = one('[data-popup-review-content]');
    const card = one('[data-popup-card]');
    const canvas = one('[data-popup-canvas]');
    const selectionLayer = one('[data-popup-selection-layer]');
    const reviewPane = one('[data-popup-review]');
    const samplePhoto = (() => {
      const img = one('[data-popup-photo-img]');
      return img ? { src: img.getAttribute('src'), srcset: img.getAttribute('srcset'), alt: img.getAttribute('alt'),
        thumb: one('[data-popup-photo-thumb]')?.getAttribute('src') } : null;
    })();
    // The preview and the checklist must agree on how faint the small text is.
    if (card) card.style.setProperty('--pe-popup-muted', String(rules.appearance.mutedOpacity));
    const textareaFits = new WeakMap();
    const listeners = [];
    const listen = (el, event, callback, options) => {
      if (!el) return;
      el.addEventListener(event, callback, options);
      listeners.push(() => el.removeEventListener(event, callback, options));
    };
    const track = name => doc.dispatchEvent(new view.CustomEvent('myuser:tool-event', { detail: { name } }));
    const canEdit = () => interaction === 'edit' && !stash;
    function notice(message) { text('[data-popup-notice]', message); }
    function save() {
      if (suppressStorage || !dirty) return;
      if (!storage) { storageSaved = false; notice('This browser cannot save your draft. Download your notes before you leave.'); return; }
      // During the sample makeover the stored draft stays the visitor's own.
      try { storage.setItem(draftStorageKey, JSON.stringify(stash || config)); storageSaved = true; }
      catch (_) { storageSaved = false; }
      notice(storageSaved ? 'Saved in this tab. Download your notes to keep your work.'
        : 'This browser cannot save your draft. Download your notes before you leave.');
    }
    function fitTextareas(force = false) {
      if (!pageScroll || destroyed) return;
      all('textarea').forEach(field => {
        const width = field.clientWidth;
        if (!width) return;
        const previous = textareaFits.get(field);
        // Focus, input, mode changes and details toggles can request the same
        // fit in one edit. Only changed copy/width needs a write-layout-read.
        if (force !== true && previous?.width === width && previous.value === field.value) return;
        // Use the full value, including new lines, without an inner scroll bar.
        const style = view.getComputedStyle(field);
        const border = (parseFloat(style.borderTopWidth) || 0) + (parseFloat(style.borderBottomWidth) || 0);
        field.style.height = 'auto';
        field.style.height = Math.ceil(field.scrollHeight + border) + 'px';
        textareaFits.set(field, { width, value: field.value });
      });
    }
    // The side-by-side workspace has a fixed height. A phone-shaped card, or a
    // big heading, can be taller than it: the Mobile frame used to cut off
    // "No thanks" behind an inner scroll. Zoom the card to fit instead.
    function fitCard() {
      if (!card || !canvas || destroyed) return;
      if (card.style.zoom) card.style.removeProperty('zoom');
      if (stacked() || root.dataset.makeover) { delete canvas.dataset.fit; return; }
      const style = view.getComputedStyle(canvas);
      const room = canvas.clientHeight - (parseFloat(style.paddingTop) || 0) - (parseFloat(style.paddingBottom) || 0);
      const height = card.offsetHeight;
      if (!room || !height || height <= room) { delete canvas.dataset.fit; return; }
      const scale = Math.max(0.6, Math.floor(room / height * 100) / 100);
      card.style.zoom = String(scale);
      canvas.dataset.fit = String(scale);
    }
    function show(el, visible) { if (el) el.hidden = !visible; }
    function text(selector, value) { all(selector).forEach(el => { if (el.textContent !== value) el.textContent = value; }); }
    function revealInPage(target, block = 'nearest') {
      if (homepage) return;
      if (!target?.scrollIntoView) return;
      const bounds = target.getBoundingClientRect();
      const header = parseFloat(view.getComputedStyle(doc.documentElement).scrollPaddingTop) || 0;
      const bottom = view.innerHeight || doc.documentElement.clientHeight;
      if (bounds.top >= header && bounds.bottom <= bottom) return;
      target.scrollIntoView({ behavior: media.matches ? 'instant' : 'smooth', block, inline: 'nearest' });
    }
    function focusReviewControl(selector) {
      const control = one(selector);
      control?.focus({ preventScroll: true });
      revealWithin(reviewPane, control);
      if (pageScroll) revealInPage(control);
    }
    function popupText() {
      return [
        'Store name: ' + (config.storeName || '(none)'),
        'Heading: ' + config.heading,
        'Description: ' + config.subheading,
        'Button: ' + config.cta,
        'Dismiss link: ' + (config.decline || '(none)'),
        'Thank-you message: ' + config.thanks
      ].join('\n\n');
    }
    function buildNotes() {
      const result = rules.evaluate(config);
      const sample = rules.defaults.storeName;
      // Renaming the store leaves the sample's name in any copy nobody retyped.
      const leftovers = config.storeName === sample ? [] : [['subheading', 'description'], ['thanks', 'thank-you message'], ['heading', 'heading'], ['cta', 'button'], ['decline', 'dismiss link']]
        .filter(([key]) => new RegExp('\\b' + sample + '\\b', 'i').test(config[key])).map(([, name]) => name);
      return [
        'EMAIL SIGN-UP POP-UP NOTES — myuser.ai',
        'YOUR TEXT\n' + popupText() + (config.storeName === sample ? '\n\n(' + sample + ' is the sample store. Use your own store name.)'
          : leftovers.length ? '\n\n(Your ' + leftovers.join(' and ') + ' still mention' + (leftovers.length === 1 ? 's ' : ' ') + sample + ', the sample store.)' : ''),
        'DESIGN\n' + [
          'Photo: ' + (config.layout !== 'split' ? 'No photo.' : photoURL ? 'On the left: your own photo (not included in this file).'
            : 'On the left. The sample photo is a placeholder; use your own.'),
          'Background: ' + config.background, 'Text: ' + config.textColor,
          'Button: ' + config.buttonColor, 'Button text: ' + config.buttonTextColor,
          'Heading font: ' + config.font, 'Heading size: ' + config.headingSize + ' px'
        ].join('\n'),
        'SIGN-UP SETTINGS\n' + [
          'Required fields: ' + (config.firstName ? 'First name and email' : 'Email only'),
          'Show after: ' + (config.delay ? config.delay + ' seconds' : 'Page opens'),
          'Show once per visit: ' + (config.showOnce ? 'Yes' : 'No'),
          'Skip people already on the list: ' + (config.excludeSubscribers ? 'Yes' : 'No')
        ].join('\n'),
        'CHECKLIST: ' + result.score + '/100' + (result.capped ? ' (held at ' + rules.fixCap + ' while an item needs a fix)' : '') + '\n' +
          result.checks.map(row => row.symbol + ' ' + row.label + ' — ' + row.title + ': ' + row.summary + (row.scored ? '' : ' (not scored)')).join('\n') +
          '\nTiming (not scored): ' + result.timing,
        'USE THIS IN YOUR STORE\n1. Open your store’s pop-up tool and copy the text and settings above.\n2. Use your own photo, and set up the email or code that delivers what the pop-up offers.\n3. Check the form, thank-you screen and welcome email on desktop and mobile before you turn it on.',
        'ABOUT THIS FILE\nThis is a reference, not an install file. Nothing here connects to your store or to a myuser.ai account. No sign-up or email was sent. Timing and audience rules were saved, not run in the preview. The checklist looks at the basics it can see in this pop-up; its score does not predict sign-ups or sales.',
        'Open the editor: https://myuser.ai/tools/popup-editor'
      ].join('\n\n') + '\n';
    }
    function openHandoff(focusHeading = true) {
      const handoff = one('[data-popup-handoff]');
      if (!handoff) return;
      show(handoff, true);
      all('[data-popup-download-notes][aria-controls]').forEach(button => button.setAttribute('aria-expanded', 'true'));
      if (focusHeading) {
        revealInPage(handoff);
        handoff.querySelector('h3').focus({ preventScroll: true });
      }
    }
    async function copyText() {
      const copy = popupText();
      try {
        if (!view.navigator.clipboard || !view.navigator.clipboard.writeText) throw new Error('Clipboard unavailable');
        await view.navigator.clipboard.writeText(copy);
        if (destroyed) return;
        show(one('[data-popup-copy-fallback]'), false);
        notice('Pop-up text copied. Paste it into your store’s pop-up tool.');
        track('homepage_popup_text_copied');
      } catch (_) {
        if (destroyed) return;
        openHandoff(false);
        show(one('[data-popup-copy-fallback]'), true);
        const field = one('[data-popup-copy-text]');
        if (!field) return;
        field.value = copy;
        fitTextareas();
        field.focus({ preventScroll: true }); field.select();
        revealInPage(field);
        notice('Copy was blocked. Your text is selected below so you can copy it.');
      }
    }
    function download(content, type, filename, message) {
      try {
        if (downloadURL) { view.URL.revokeObjectURL(downloadURL); downloadURL = null; }
        downloadURL = view.URL.createObjectURL(new view.Blob([content], { type }));
        const link = doc.createElement('a'); link.href = downloadURL; link.download = filename;
        doc.body.appendChild(link); link.click(); link.remove();
        notice(message);
        track('homepage_popup_downloaded');
        return true;
      } catch (_) {
        openHandoff(false);
        show(one('[data-popup-copy-fallback]'), true);
        const field = one('[data-popup-copy-text]');
        if (!field) return false;
        field.value = content; field.focus({ preventScroll: true }); field.select();
        fitTextareas();
        revealInPage(field);
        notice('Download was blocked. Your notes are selected below so you can copy them.');
        return false;
      }
    }
    function downloadNotes() {
      if (download(buildNotes(), 'text/plain;charset=utf-8', 'myuser-popup-notes.txt', 'Notes downloaded. Use them in your store’s pop-up tool.')) openHandoff();
    }
    function tryError(message, field) {
      const box = one('[data-popup-try-error]');
      all('[data-popup-try-field]').forEach(input => input.removeAttribute('aria-invalid'));
      if (box) { box.textContent = message; box.hidden = !message; }
      if (message && field) {
        field.setAttribute('aria-invalid', 'true');
        field.setAttribute('aria-describedby', box?.id || '');
      }
    }
    // Try it checks the form the way a store would, on this page only: no
    // request is made and nothing is stored.
    function trySubmit() {
      const name = one('[data-popup-try-field="name"]'), email = one('[data-popup-try-field="email"]');
      if (config.firstName && name && !name.value.trim()) { tryError('Enter a first name.', name); name.focus({ preventScroll: true }); return; }
      if (email && !/^[^\s@]+@[^\s@.]+(\.[^\s@.]+)*\.[a-z]{2,}$/i.test(email.value.trim())) {
        tryError('Enter an email address like name@example.com.', email); email.focus({ preventScroll: true }); return;
      }
      tryError('');
      preview('success');
      track('homepage_popup_tried');
    }
    function preview(state = 'form') {
      if (state !== 'form') clearEditing();
      root.dataset.previewState = state;
      show(card, state !== 'closed');
      show(one('[data-popup-reopen]'), state === 'closed');
      show(one('[data-popup-form-view]'), state !== 'success');
      show(one('[data-popup-success]'), state === 'success');
      show(one('[data-popup-try-again]'), interaction === 'try' && state !== 'form');
      if (state === 'form') tryError('');
      fitCard();
    }
    function paintPhoto() {
      const img = one('[data-popup-photo-img]');
      if (!img || !samplePhoto) return;
      // The sample makeover shows the sample's own photo; the visitor's comes back after.
      const own = !stash && photoURL;
      const src = own ? photoURL : samplePhoto.src;
      if (img.getAttribute('src') !== src) {
        if (own) img.removeAttribute('srcset'); else img.setAttribute('srcset', samplePhoto.srcset);
        img.setAttribute('src', src);
        img.setAttribute('alt', own ? 'Your photo' : samplePhoto.alt);
      }
      show(one('[data-popup-photo-caption]'), !own);
      const thumb = one('[data-popup-photo-thumb]');
      if (thumb && thumb.getAttribute('src') !== (photoURL || samplePhoto.thumb)) thumb.setAttribute('src', photoURL || samplePhoto.thumb);
      text('[data-popup-photo-name]', photoURL ? photoName : 'Sample photo');
      show(one('[data-popup-photo-reset]'), !!photoURL);
    }
    function choosePhoto(input) {
      const file = input.files && input.files[0];
      if (!file) return;
      input.value = '';
      if (!/^image\//.test(file.type || '')) { notice('Choose an image file, such as a JPG or PNG.'); return; }
      if (photoURL) view.URL.revokeObjectURL(photoURL);
      // An object URL points at the file on this device. Nothing is uploaded,
      // and it is not written into the saved draft.
      photoURL = view.URL.createObjectURL(file);
      photoName = Array.from(file.name || 'Your photo').slice(0, 40).join('');
      if (config.layout !== 'split') edited('layout', 'split', 'settings');
      else paintPhoto();
      notice('Your photo is in the preview. It stays on this device and isn’t saved with the draft.');
    }
    function resetPhoto() {
      if (photoURL) view.URL.revokeObjectURL(photoURL);
      photoURL = null; photoName = '';
      paintPhoto();
      one('[data-popup-photo-input]')?.focus({ preventScroll: true });
    }
    function renderConfig() {
      all('[data-popup-field]').forEach(input => {
        const key = input.dataset.popupField;
        if (!(key in config)) return;
        if (input.type === 'checkbox') input.checked = config[key];
        else if (input.value !== String(config[key])) input.value = config[key];
      });
      // Keep the focused text node: replacing it would lose its native caret.
      paintCard(card, config, doc.activeElement);
      paintPhoto();
      text('[data-popup-value="headingSize"]', config.headingSize + ' px');
      text('[data-popup-value="delay"]', config.delay === 0 ? 'Right away' : config.delay + ' seconds');
      text('[data-popup-summary="text"]', config.heading || 'Add a heading');
      // The summary repeats the option the visitor picked, in the same words.
      text('[data-popup-summary="layout"]', config.layout === 'split' ? 'Photo on the left' : 'No photo');
      text('[data-popup-summary="signup"]', config.firstName ? 'Name + email' : 'Email only');
      text('[data-popup-summary="when"]', config.delay === 0 ? 'Right away' : 'After ' + config.delay + 's');
      text('[data-popup-summary="style"]', config.font + ' · ' + config.headingSize + 'px');
      ['background', 'textColor', 'buttonColor', 'buttonTextColor'].forEach(key => text('[data-popup-value="' + key + '"]', config[key]));
      all('[data-popup-count]').forEach(el => {
        const key = el.dataset.popupCount, count = String(config[key]).length;
        el.textContent = count + ' / ' + copyFields[key][1] + (count > 120 ? ' · over 120, worth trimming' : ' · aim for 120 or fewer');
        el.dataset.over = String(count > 120);
      });
      text('[data-popup-name]', (config.storeName || 'Your store') + ' sign-up pop-up');
      text('[data-popup-url]', storeSlug(config.storeName));
      text('[data-popup-backdrop-name]', config.storeName || 'Your store');
      fitTextareas();
      save();
      renderFollow();
      fitCard();
    }
    function editHint() {
      const key = root.dataset.editingField;
      // In review, the footer names the finding outlined on the pop-up, so the
      // outline never needs a label drawn over the card itself.
      const outlined = mode === 'review' && reviewTargets.length && report.checks.find(row => row.id === selected);
      text('[data-popup-edit-hint]', stash ? 'A scripted makeover of the sample. Your pop-up comes back when it ends.'
        : interaction === 'try' ? 'Try the form. Nothing is sent: the thank-you screen is only a preview.'
          : key && copyFields[key] ? 'Editing ' + copyFields[key][0].toLowerCase() + ' · Changes show on both sides.'
            : outlined ? 'Outlined: ' + outlined.title + ' (' + outlined.label.toLowerCase() + '). Click any words to edit them.'
              : mode === 'review' ? 'Click any words to edit them. The checklist updates as you type.'
                : 'Click text to edit it right here.');
      show(one('[data-popup-edit-done]'), !!key && canEdit());
      show(one('[data-popup-view-settings]'), !!copyFields[key] && mode === 'edit' && canEdit());
    }
    function clearEditing(quiet = false) {
      delete root.dataset.editingField;
      all('.pe-field-active, .pe-preview-active').forEach(el => el.classList.remove('pe-field-active', 'pe-preview-active'));
      selectionLayer?.replaceChildren();
      if (!quiet) editHint();
    }
    function setInteraction(next) {
      interaction = next;
      root.dataset.interaction = next;
      const editable = canEdit();
      all('[data-popup-inline]').forEach(el => {
        el.setAttribute('contenteditable', editable ? 'plaintext-only' : 'false');
        el.setAttribute('tabindex', editable ? '0' : '-1');
        if (editable) {
          el.setAttribute('role', 'textbox');
          el.setAttribute('aria-multiline', String(el.dataset.popupInline === 'subheading'));
          el.setAttribute('title', 'Click to edit');
        } else {
          el.removeAttribute('role'); el.removeAttribute('aria-multiline'); el.removeAttribute('title');
        }
      });
      all('[data-popup-focus-field]').forEach(el => {
        el.setAttribute('tabindex', editable ? '0' : '-1');
        if (editable) el.setAttribute('role', 'button');
        else el.removeAttribute('role');
      });
      // In edit mode the button's label is the text box, not a second action.
      all('[data-popup-submit], .pe-popup-decline').forEach(el => el.tabIndex = editable ? -1 : 0);
      // Try it makes the sample fields real inputs; editing keeps them inert.
      all('[data-popup-try-field]').forEach(input => { input.readOnly = next !== 'try'; input.tabIndex = next === 'try' ? 0 : -1; });
      all('[data-popup-interaction]').forEach(el => el.setAttribute('aria-pressed', String(el.dataset.popupInteraction === next)));
      if (!editable) {
        if (doc.activeElement?.matches('[data-popup-inline]')) doc.activeElement.blur();
        const selection = view.getSelection();
        if (selection && all('[data-popup-inline]').some(el => el.contains(selection.anchorNode) || el.contains(selection.focusNode))) selection.removeAllRanges();
        clearEditing();
      }
      if (next === 'try') clearHighlight();
      else if (mode === 'review' && !stash) highlight(report.checks.find(row => row.id === selected));
      editHint();
    }
    function targetFor(key) {
      const selectors = { layout: '[data-popup-image]', firstName: '.pe-capture', background: '[data-popup-card]',
        textColor: '[data-popup-inline="subheading"]', buttonColor: '[data-popup-submit]', buttonTextColor: '[data-popup-submit]',
        font: '[data-popup-inline="heading"]', headingSize: '[data-popup-inline="heading"]', storeName: '[data-popup-brand]',
        thanks: '[data-popup-preview-field="thanks"]' };
      return one(inlineFields.has(key) ? `[data-popup-inline="${key}"]` : selectors[key] || '[data-popup-card]');
    }
    function revealWithin(container, target, center = false) {
      // The homepage preview stays in the page flow. Its right-hand settings
      // may scroll so clicking the preview can still reveal the matching field.
      if (pageScroll && !container?.matches('.pe-custom,.pe-review')) return;
      if (!container || !target || container.scrollHeight <= container.clientHeight) return;
      const box = container.getBoundingClientRect(), bounds = target.getBoundingClientRect();
      if (bounds.top >= box.top + 22 && bounds.bottom <= box.bottom - 22) return;
      const delta = center && bounds.height < box.height - 44
        ? (bounds.top + bounds.bottom - box.top - box.bottom) / 2
        : bounds.top < box.top + 22 ? bounds.top - box.top - 22 : bounds.bottom - box.bottom + 22;
      // Scroll only this editor pane. scrollIntoView would also move the page.
      container.scrollTop += delta;
    }
    function selectField(key, source) {
      const input = one(`[data-popup-field="${key}"]`);
      if (!input) return;
      const changed = root.dataset.editingField !== key;
      clearEditing(true);
      root.dataset.editingField = key;
      input.closest('details').open = true;
      (input.closest('.pe-field, .pe-color-field, .pe-check-row') || input).classList.add('pe-field-active');
      const target = targetFor(key);
      if (target && !target.hidden) target.classList.add('pe-preview-active');
      if (source === 'preview') {
        // Keep the real caret on the pop-up. Opening Design never steals focus.
        if (!stacked() && mode === 'edit') revealWithin(one('[data-popup-edit]'), input.closest('.pe-field') || input, true);
      } else if (changed && !stacked()) revealWithin(canvas, target, true);
      editHint();
    }
    function textPoint(el, offset) {
      const walker = doc.createTreeWalker(el, 4); // SHOW_TEXT; offsets match input.selectionStart.
      let node, last;
      while ((node = walker.nextNode())) {
        last = node;
        if (offset <= node.length) return [node, offset];
        offset -= node.length;
      }
      return last ? [last, last.length] : [el, 0];
    }
    function paintSelection() {
      selectionLayer?.replaceChildren();
      const input = doc.activeElement;
      if (!canEdit() || !input?.matches('[data-popup-field]') || !copyFields[input.dataset.popupField]) return;
      const target = targetFor(input.dataset.popupField);
      if (!target || card.hidden || !target.matches('[data-popup-inline], [data-popup-brand]') || root.dataset.previewState !== 'form') return;
      const range = doc.createRange();
      if (!range.getClientRects || !selectionLayer) return;
      const start = input.selectionStart ?? 0, end = input.selectionEnd ?? start;
      range.setStart(...textPoint(target, start)); range.setEnd(...textPoint(target, end));
      const base = card.getBoundingClientRect();
      // A zoomed-to-fit card draws its marks in its own, unzoomed pixels.
      const zoom = parseFloat(card.style.zoom) || 1;
      function mark(rect, kind) {
        const el = doc.createElement('span');
        el.dataset[kind === 'caret' ? 'popupCaret' : 'popupSelection'] = '';
        el.className = 'pe-mirror-' + kind;
        Object.assign(el.style, { left: (rect.left - base.left) / zoom + 'px', top: (rect.top - base.top) / zoom + 'px',
          width: (kind === 'caret' ? 2 : rect.width / zoom) + 'px', height: rect.height / zoom + 'px' });
        selectionLayer.appendChild(el);
      }
      if (start !== end) Array.from(range.getClientRects()).filter(r => r.width && r.height).forEach(r => mark(r, 'selection'));
      range.collapse(input.selectionDirection === 'backward');
      let caret = range.getClientRects()[0];
      if (!caret?.height) {
        const bounds = target.getBoundingClientRect(), style = view.getComputedStyle(target);
        const size = (parseFloat(style.fontSize) || 14) * zoom;
        caret = { left: style.textAlign === 'center' ? bounds.left + bounds.width / 2 : bounds.left,
          top: bounds.top, height: size * 1.1, width: 0 };
      }
      mark(caret, 'caret');
      if (!stacked()) {
        // Long or multi-line copy follows the caret, not the whole text block.
        revealWithin(canvas, { getBoundingClientRect: () => ({ top: caret.top, bottom: caret.top + caret.height, height: caret.height }) });
      }
    }
    function edited(key, value, source) {
      // Anything typed belongs to the visitor's own pop-up, never the sample makeover.
      if (stash) leaveMakeover();
      clearChange();
      config[key] = copyFields[key] ? draftText(key, value) : rules.normalize({ ...config, [key]: value })[key];
      dirty = true;
      show(one('[data-popup-copy-fallback]'), false);
      renderConfig(); preview(key === 'thanks' ? 'success' : 'form'); setInteraction('edit');
      // Review stays open while the visitor edits the pop-up beside it, and
      // follows along: the findings used to be a dead end that froze the preview.
      if (mode === 'review') renderReview();
      selectField(key, source); paintSelection();
    }
    function readInline(el) { return el.innerText ?? el.textContent ?? ''; }
    function writeInline(el) {
      if (composing) return;
      const key = el.dataset.popupInline;
      const value = readInline(el), clean = draftText(key, value);
      if (clean !== value) {
        el.textContent = clean;
        const range = doc.createRange(); range.selectNodeContents(el); range.collapse(false);
        const selection = view.getSelection(); selection.removeAllRanges(); selection.addRange(range);
      }
      edited(key, clean, 'preview');
    }
    function clearHighlight() {
      reviewTargets.forEach(el => { el.classList.remove('pr-target'); delete el.dataset.reviewStatus; });
      reviewTargets = [];
      show(one('[data-popup-move-badge]'), false);
      root.removeAttribute('data-review-target');
    }
    function setMode(next) {
      mode = next;
      root.dataset.mode = next;
      show(one('[data-popup-edit]'), next === 'edit');
      show(reviewPane, next === 'review');
      all('[data-popup-mode]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.popupMode === next)));
      if (next === 'edit') clearHighlight();
      else followCheck = null;
      fitTextareas();
      setInteraction(interaction);
      renderUndo();
      renderFollow();
    }
    function stacked() {
      const previewColumn = one('.pe-preview-col');
      const rail = one('.pe-rail');
      return rail && previewColumn && rail.getBoundingClientRect().top >= previewColumn.getBoundingClientRect().bottom;
    }

    // ── Checklist ─────────────────────────────────────────────────────────
    const railId = reviewPane?.id || 'popup-review';
    const firstOpen = () => (report.checks.find(row => row.status === 'fix') || report.checks.find(row => row.status === 'improve') || report.checks[0]).id;
    function highlight(check) {
      clearHighlight();
      if (!check || mode !== 'review' || interaction !== 'edit' || stash) return;
      root.dataset.reviewTarget = check.id;
      const selectors = {
        offer: '[data-popup-preview-field="heading"], [data-popup-preview-field="subheading"]',
        cta: '[data-popup-submit]',
        heading: '[data-popup-preview-field="heading"]',
        subheading: '[data-popup-preview-field="subheading"]',
        fields: '.pe-capture',
        decline: '.pe-popup-decline',
        'readability:heading': '[data-popup-preview-field="heading"]',
        'readability:description': '[data-popup-preview-field="subheading"], .pe-popup-consent, .pe-popup-decline',
        'readability:label': '[data-popup-submit]',
        'readability:button': '[data-popup-submit]',
        'readability:all': '[data-popup-card]'
      };
      const keys = check.target.startsWith('readability:') ? check.target.slice(12).split(',').map(key => 'readability:' + key) : [check.target];
      reviewTargets = [...new Set(keys.flatMap(key => all(selectors[key] || '[data-popup-card]')))].filter(el => !el.hidden);
      reviewTargets.forEach(el => { el.classList.add('pr-target'); el.dataset.reviewStatus = check.status; });
      // The homepage has no preview footer to name the outline, so a small
      // label sits in the canvas corner there instead.
      const badge = one('[data-popup-move-badge]');
      if (badge && !one('[data-popup-edit-hint]')) {
        badge.textContent = check.symbol + ' ' + check.label + ' · ' + check.title;
        badge.dataset.status = check.status;
        show(badge, true);
      }
      editHint();
    }
    function changeLabel(fix) {
      const patch = fix.patch;
      if ('cta' in patch) return 'Changed button text to “' + patch.cta + '”';
      if ('decline' in patch) return 'Changed the dismiss link to “' + patch.decline + '”';
      if ('firstName' in patch) return 'Now asking for email only';
      if ('headingSize' in patch) return 'Changed the heading size to ' + patch.headingSize + 'px';
      const names = { textColor: 'text color', buttonColor: 'button color', buttonTextColor: 'button text color' };
      return 'Changed the ' + Object.keys(patch).map(key => names[key] || key).join(' and ');
    }
    function swatches(fix) {
      const colors = fix.changes.filter(item => /^#[0-9a-f]{6}$/.test(item.from) && /^#[0-9a-f]{6}$/.test(item.to));
      if (!colors.length) return '';
      return `<span class="pr-swatches" aria-hidden="true">${colors.map(item => `<i style="background:${item.from}"></i><b>→</b><i style="background:${item.to}"></i>`).join('<b>·</b>')}</span>` +
        `<span class="pe-visually-hidden">: ${colors.map(item => esc(item.label) + ' from ' + item.from + ' to ' + item.to).join('; ')}</span>`;
    }
    function undoLine(label) {
      return `<p class="pe-undo pr-undo-local" role="status"><span>${esc(label)}</span><button type="button" data-popup-undo-button>Undo</button></p>`;
    }
    function checkBody(row) {
      const id = railId + '-check-' + row.id;
      const selectedRow = row.id === selected;
      const pairs = row.parts.length ? `<ul class="pr-pairs" aria-label="Contrast, measured as drawn">${row.parts.map(part => `<li data-status="${part.status}"><span class="pr-sample${part.id === 'button' ? ' pr-sample-shape' : ''}" style="background:${part.back};color:${part.drawn}" aria-hidden="true">${part.id === 'button' ? `<i style="background:${part.front}"></i>` : 'Aa'}</span><span class="pr-pair-name">${esc(part.label)}</span><span class="pr-pair-ratio">${part.ratioText}</span><span class="pr-pair-mark" data-status="${part.status}"><span aria-hidden="true">${part.status === 'pass' ? '✓' : part.status === 'fix' ? '✕' : '↑'}</span><span class="pe-visually-hidden">${part.status === 'pass' ? 'Pass' : part.status === 'fix' ? 'Fix' : 'Could be better'}</span></span></li>`).join('')}</ul>` : '';
      const examples = row.examples.length ? `<div class="pr-examples"><p>${row.status === 'improve' && row.offer ? 'For example:' : 'Examples of a concrete reason\u00a0— use one you can really offer:'}</p><ul>${row.examples.map(item => `<li>${esc(item)}</li>`).join('')}</ul></div>` : '';
      const fixes = row.fixes.length ? `<div class="pr-fixes" role="group" aria-label="${row.status === 'pass' ? 'Optional change' : 'Suggested changes'}">${row.fixes.map(fix => `<button type="button" class="pr-fix${fix.optional ? ' pr-fix-quiet' : ''}" data-popup-fix="${esc(fix.id)}"><span>${esc(fix.label)}</span>${swatches(fix)}</button>`).join('')}</div>` : '';
      const undoHere = lastChange && lastChange.check === row.id ? undoLine(lastChange.label) : '';
      return `<li class="pr-item" data-status="${row.status}"${selectedRow ? ' data-selected' : ''}>
        <button type="button" class="pr-row" data-popup-check="${row.id}" aria-expanded="${selectedRow}" aria-controls="${id}"><span class="pr-mark" aria-hidden="true">${row.symbol}</span><span class="pr-row-main"><span class="pr-row-title">${esc(row.title)}</span><span class="pr-row-summary"><span class="pr-row-status">${esc(row.label)}</span><span aria-hidden="true"> · </span>${esc(row.summary)}</span></span><svg class="pr-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="m6 9 6 6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></button>
        <div class="pr-body" id="${id}"${selectedRow ? '' : ' hidden'}>${undoHere}<p class="pr-detail">${esc(row.detail)}</p>${pairs}${examples}${fixes}${row.scored ? '' : '<p class="pr-note">Shown for reference. It isn’t part of the score.</p>'}<div class="pr-actions"><button type="button" class="pr-edit" data-popup-edit-check="${row.id}">${esc(row.editLabel)}</button><button type="button" class="pr-locate" data-popup-locate="${row.id}">See it on the pop-up <span aria-hidden="true">↑</span></button></div></div>
      </li>`;
    }
    function renderReview(message = '') {
      if (!panel) return;
      report = rules.evaluate(config);
      if (!report.checks.some(row => row.id === selected)) selected = firstOpen();
      root.dataset.reviewState = 'ready';
      const counts = [['pass', report.counts.pass, ' pass'], ['improve', report.counts.improve, ' could be better'], ['fix', report.counts.fix, ' to fix']]
        .filter(([, count]) => count).map(([status, count, label]) => `<span data-status="${status}">${count}${label}</span>`).join('<span aria-hidden="true"> · </span>');
      const scored = report.checks.filter(row => row.scored);
      const sample = !makeover ? '' : confirming
        ? `<div class="pr-demo" data-confirming><p>The makeover plays on the Solara sample, not on your pop-up. Yours steps aside while it plays and comes back when it ends. Nothing you made changes unless you choose “Keep these edits”.</p><div class="pr-demo-actions"><button type="button" class="pr-demo-start" data-popup-makeover-confirm>Play the sample makeover</button><button type="button" class="pr-edit" data-popup-makeover-cancel>Not now</button></div></div>`
        : `<div class="pr-demo"><p><strong>See the basics on a sample.</strong> A scripted makeover of the Solara sample: four edits, one checklist item at a time. Your pop-up steps aside while it plays.</p><button type="button" class="pr-demo-start" data-popup-makeover-start>Watch a sample makeover</button></div>`;
      panel.innerHTML = `<div class="pr-summary"><p class="pr-score" aria-label="Checklist score: ${report.score} out of 100"><strong>${report.score}</strong><span>/100</span></p><div><p class="pr-score-title">Checklist score</p><p class="pr-counts">${counts}</p></div></div>
        ${report.capped ? `<p class="pr-cap">Held at ${rules.fixCap} while ${report.counts.fix === 1 ? 'an item needs' : 'items need'} a fix.</p>` : ''}
        <ol class="pr-list" aria-label="Checklist for your pop-up">${report.checks.map(checkBody).join('')}</ol>
        <div class="pr-timing"><p><span>When it shows · not scored</span>${esc(report.timing)}</p><button type="button" class="pr-edit" data-popup-edit-field="delay">Edit timing</button></div>
        <details class="pr-rubric"><summary>What this checklist checks</summary><p>Six checks, run in your browser on the pop-up you made. They look at what they can see\u00a0— your words, the colors as drawn, the fields\u00a0— not whether your offer appeals or how your shoppers will respond. The score doesn’t predict sign-ups or sales.</p><ul>${scored.map(row => `<li>${esc(row.title)}: ${rules.weights[row.id][0]} points, ${rules.weights[row.id][1]} if it could be better</li>`).join('')}<li>Quick to fill in, and timing: shown, not scored</li></ul><p>Contrast uses the WCAG formula. Any item marked Fix holds the score at ${rules.fixCap} or below.</p></details>
        ${sample}
        ${homepage ? '' : `<button type="button" class="pr-download" data-popup-download-notes>${'<svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M8 1v9M4 6l4 4 4-4M2 11v4h12v-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>'}<span>Download my pop-up notes</span></button>`}
        <p class="pe-visually-hidden" role="status">${esc(message)}</p>`;
      highlight(report.checks.find(row => row.id === selected));
      renderUndo();
    }
    function startReview() {
      if (stash) leaveMakeover();
      confirming = false;
      if (doc.activeElement?.matches('[data-popup-inline]')) doc.activeElement.blur();
      const selection = view.getSelection();
      if (selection && all('[data-popup-inline]').some(el => el.contains(selection.anchorNode) || el.contains(selection.focusNode))) selection.removeAllRanges();
      clearEditing();
      preview(); setMode('review');
      report = rules.evaluate(config);
      selected = firstOpen();
      // Results are computed synchronously, so they appear at once. A 1.7s
      // "Checking…" animation used to replay on every tab switch.
      renderReview();
      if (reviewPane) reviewPane.scrollTop = 0;
      track('homepage_popup_reviewed');
    }
    function showChecklist() {
      if (stash) leaveMakeover();
      if (mode !== 'review') { preview(); setMode('review'); }
      renderReview();
    }
    function selectCheck(id, focus = true) {
      if (!report.checks.some(row => row.id === id)) return;
      selected = id;
      renderReview();
      if (focus) focusReviewControl(`[data-popup-check="${id}"]`);
    }
    function applyFix(id) {
      const fix = rules.findFix(config, id);
      if (!fix) return;
      const before = { ...config };
      const row = rules.evaluate(before).checks.find(item => item.fixes.some(entry => entry.id === id));
      const clean = rules.normalize({ ...config, ...fix.patch });
      // Only the failing property changes. Fixes used to paste the sample's
      // skincare-guide copy over the merchant's words and reset their colors.
      Object.keys(fix.patch).forEach(key => { config[key] = copyFields[key] ? draftText(key, clean[key]) : clean[key]; });
      dirty = true;
      renderConfig(); preview();
      setChange(changeLabel(fix), before, row ? row.id : null);
      if (row) selected = row.id;
      renderReview();
      focusReviewControl(`[data-popup-check="${selected}"]`);
      track('homepage_popup_fix_applied');
    }
    function editFromCheck(id) {
      const row = report.checks.find(item => item.id === id);
      if (!row) return;
      editField(row.field, id);
    }
    function editField(key, from = null) {
      if (stash) leaveMakeover();
      const input = one(`[data-popup-field="${key}"]`);
      if (!input) return;
      setMode('edit'); setInteraction('edit'); preview(key === 'thanks' ? 'success' : 'form');
      followCheck = from;
      const group = input.closest('details');
      if (group) group.open = true;
      input.focus({ preventScroll: true });
      selectField(key, 'settings');
      renderFollow();
      if (pageScroll || stacked()) revealInPage(input, 'center');
      else revealWithin(one('[data-popup-edit]'), input.closest('.pe-field, .pe-color-field, .pe-check-row') || input, true);
    }
    // While a finding's field is open in Design, its status sits beside the
    // field and changes as the visitor types.
    let followBanner = null;
    function renderFollow() {
      if (!followBanner && followCheck) {
        followBanner = doc.createElement('div');
        followBanner.className = 'pe-follow';
        followBanner.innerHTML = '<span class="pe-follow-mark" aria-hidden="true"></span><span class="pe-follow-text"><span class="pe-follow-status" role="status"></span><span class="pe-follow-title"></span></span><button type="button" data-popup-back-to-checklist>Back to checklist</button>';
      }
      if (!followBanner) return;
      if (!followCheck || mode !== 'edit' || stash) { followBanner.hidden = true; return; }
      const row = rules.evaluate(config).checks.find(item => item.id === followCheck);
      const body = row && one(`[data-popup-field="${row.field}"]`)?.closest('.pe-acc-body');
      if (!row || !body) { followBanner.hidden = true; return; }
      if (followBanner.parentElement !== body) body.prepend(followBanner);
      followBanner.dataset.status = row.status;
      followBanner.querySelector('.pe-follow-mark').textContent = row.symbol;
      const status = followBanner.querySelector('.pe-follow-status'), title = followBanner.querySelector('.pe-follow-title');
      if (status.textContent !== row.label) status.textContent = row.label;
      if (title.textContent !== row.title + ': ' + row.summary) title.textContent = row.title + ': ' + row.summary;
      followBanner.hidden = false;
    }
    function locate(id) {
      if (selected !== id) selectCheck(id, false);
      const target = reviewTargets[0];
      if (target) revealInPage(target, 'center');
    }

    // ── Undo ──────────────────────────────────────────────────────────────
    function setChange(label, before, check = null, slot = check ? 'review' : 'header') {
      lastChange = { label, before: { ...before }, check, slot };
      renderUndo();
    }
    function clearChange() {
      if (!lastChange) return;
      lastChange = null;
      renderUndo();
      all('.pr-undo-local').forEach(el => el.remove());
    }
    function renderUndo() {
      // A fix shows its Undo inside its own finding, where it was clicked. With
      // that finding closed, or for a kept makeover, it sits at the top of the
      // checklist; Reset, and anything undoable while Design is open, uses the
      // header next to the Reset button.
      const local = !!lastChange?.check && mode === 'review' && selected === lastChange.check;
      let slot = null;
      if (lastChange && !local && !stash) slot = mode === 'review' && lastChange.slot === 'review' ? 'review' : 'header';
      all('[data-popup-undo]').forEach(el => {
        const on = el.dataset.popupUndo === slot;
        el.hidden = !on;
        if (on) { const label = el.querySelector('[data-popup-undo-text]'); if (label.textContent !== lastChange.label) label.textContent = lastChange.label; }
      });
    }
    function undo(trigger) {
      if (!lastChange) return;
      const { before, check } = lastChange;
      const fromHeader = !!trigger?.closest('.pe-header');
      if (stash) leaveMakeover();
      config = { ...before }; lastChange = null; dirty = true;
      all('.pr-undo-local').forEach(el => el.remove());
      renderConfig(); preview();
      if (mode === 'review') {
        if (check) selected = check;
        renderReview('Change undone.');
        focusReviewControl(`[data-popup-check="${selected}"]`);
      } else {
        renderUndo();
        notice('Change undone.');
      }
      if (fromHeader) one('.pe-header [data-popup-reset]')?.focus({ preventScroll: true });
    }
    function reset() {
      if (stash) leaveMakeover();
      const before = { ...config };
      confirming = false; followCheck = null;
      config = rules.normalize(rules.defaults);
      dirty = true;
      if (doc.activeElement?.matches('[data-popup-inline]')) doc.activeElement.blur();
      clearEditing(); renderConfig(); preview(); setMode('edit'); setInteraction('edit');
      root.dataset.reviewState = 'idle';
      if (sameDraft(before, config)) clearChange(); else setChange('Reset to the sample', before);
      notice(storageSaved ? 'Sample restored and saved in this tab.' : 'Sample restored. Download your notes before you leave.');
    }

    // ── Sample makeover (homepage) ────────────────────────────────────────
    function startMakeover() {
      if (!makeover) return;
      confirming = false;
      // The stacked rail loses its height as soon as Design is hidden. Take
      // both measurements before changing modes or stopping a previous run.
      const footprint = { frame: one('.pe-frame').getBoundingClientRect().height, rail: one('.pe-rail').getBoundingClientRect().height };
      if (!stash) stash = { ...config };
      clearChange(); followCheck = null;
      if (doc.activeElement?.matches('[data-popup-inline]')) doc.activeElement.blur();
      clearEditing(); clearHighlight();
      preview(); setMode('review'); setInteraction('edit');
      makeover.start({ ...rules.defaults }, footprint);
      editHint();
      track('homepage_popup_makeover_started');
    }
    function requestMakeover() {
      if (!makeover) return;
      // The makeover never touches the visitor's pop-up, but it does take its
      // place for a while. Say so before it does, if they have made changes.
      if (!confirming && !sameDraft(config, rules.defaults)) {
        confirming = true; renderReview();
        focusReviewControl('[data-popup-makeover-confirm]');
        return;
      }
      startMakeover();
    }
    function leaveMakeover(keep = false) {
      if (!stash) return null;
      const previous = stash;
      const result = keep ? makeover?.getState().committed : null;
      // stop() writes its last complete edit into the sandbox copy; storage
      // keeps the visitor's draft throughout.
      suppressStorage = true;
      try { makeover?.stop(); } finally { suppressStorage = false; }
      stash = null;
      config = keep && result ? { ...result } : { ...previous };
      if (keep && result) dirty = true;
      renderConfig(); preview(); setInteraction(interaction);
      return previous;
    }
    function keepMakeover() {
      if (!stash) return;
      const previous = leaveMakeover(true);
      setMode('review');
      setChange('Kept the sample makeover', previous, null, 'review');
      selected = null;
      renderReview();
      focusReviewControl('[data-popup-check]');
    }
    function backToMine() {
      leaveMakeover();
      setMode('edit'); setInteraction('edit'); preview();
      root.dataset.reviewState = 'idle';
      one('[data-popup-mode="edit"]')?.focus({ preventScroll: true });
    }

    listen(root, 'focusin', event => {
      const el = event.target;
      if (el.matches('[data-popup-inline]') && canEdit()) {
        selectField(el.dataset.popupInline, 'preview');
      } else if (el.matches('[data-popup-field]')) {
        const key = el.dataset.popupField;
        setInteraction('edit'); preview(key === 'thanks' ? 'success' : 'form');
        selectField(key, 'settings'); paintSelection();
      }
    });
    listen(root, 'focusout', event => {
      if (event.target.matches('[data-popup-inline]') && !composing) {
        const el = event.target, key = el.dataset.popupInline;
        if (el.textContent !== config[key]) el.textContent = config[key];
      }
      const next = event.relatedTarget;
      if (event.target.matches('[data-popup-field="thanks"]') && !next?.matches('[data-popup-field="thanks"]') && root.dataset.previewState === 'success' && interaction === 'edit') preview();
      if (!next?.closest('[data-popup-inline], [data-popup-field], [data-popup-view-preview], [data-popup-view-settings], [data-popup-edit-done]')) clearEditing();
      else selectionLayer?.replaceChildren();
    });
    listen(root, 'compositionstart', event => { if (event.target.matches('[data-popup-inline]')) composing = true; });
    listen(root, 'compositionend', event => {
      if (!event.target.matches('[data-popup-inline]')) return;
      composing = false;
      if (canEdit()) writeInline(event.target);
      else {
        // A late IME commit is the visitor's typing: it lands in their own
        // draft even if the sample makeover started in the meantime.
        const el = event.target, key = el.dataset.popupInline;
        const value = draftText(key, readInline(el));
        clearChange();
        dirty = true;
        if (stash) { stash[key] = value; save(); }
        else {
          config[key] = value;
          renderConfig();
          if (mode === 'review') renderReview();
        }
      }
    });
    listen(root, 'paste', event => {
      const el = event.target.closest('[data-popup-inline]');
      if (!el || !canEdit() || !event.clipboardData) return;
      event.preventDefault();
      const selection = view.getSelection();
      if (!selection?.rangeCount || !el.contains(selection.anchorNode) || !el.contains(selection.focusNode)) return;
      const key = el.dataset.popupInline;
      const room = copyFields[key][1] - readInline(el).length + selection.toString().length;
      let value = draftText(key, event.clipboardData.getData('text/plain')).slice(0, Math.max(0, room));
      if (/[\uD800-\uDBFF]$/.test(value)) value = value.slice(0, -1);
      // insertText preserves native Undo. The Range fallback is plain text too.
      if (!doc.execCommand || !doc.execCommand('insertText', false, value)) {
        const range = selection.getRangeAt(0); range.deleteContents();
        const node = doc.createTextNode(value); range.insertNode(node); range.setStartAfter(node); range.collapse(true);
        selection.removeAllRanges(); selection.addRange(range);
      }
      writeInline(el);
    });
    listen(root, 'input', event => {
      const inline = event.target.closest('[data-popup-inline]');
      if (inline) { if (canEdit()) writeInline(inline); return; }
      const input = event.target.closest('[data-popup-field]');
      if (!input) return;
      const key = input.dataset.popupField;
      if (!(key in config)) return;
      edited(key, input.type === 'checkbox' ? input.checked : input.value, 'settings');
    });
    listen(root, 'change', event => {
      if (event.target.matches('[data-popup-photo-input]')) choosePhoto(event.target);
    });
    listen(doc, 'selectionchange', paintSelection);
    all('[data-popup-field]').forEach(input => {
      ['select', 'keyup', 'pointerup'].forEach(name => listen(input, name, paintSelection));
    });
    listen(view, 'resize', () => { fitTextareas(true); fitCard(); paintSelection(); });
    if (doc.fonts?.addEventListener) listen(doc.fonts, 'loadingdone', () => { fitTextareas(true); fitCard(); paintSelection(); });
    all('details').forEach(group => listen(group, 'toggle', fitTextareas));
    all('textarea').forEach(field => listen(field, 'input', fitTextareas));
    if (view.ResizeObserver) {
      const observer = new view.ResizeObserver(paintSelection);
      if (card) observer.observe(card);
      listeners.push(() => observer.disconnect());
    }
    listen(root, 'click', event => {
      const target = event.target;
      const inline = target.closest('[data-popup-inline]');
      if (stash && (inline || target.closest('[data-popup-submit], .pe-popup-decline, [data-popup-focus-field]') || target === card || target.matches('.pe-card-copy'))) {
        // Clicking the pop-up during the sample makeover means "let me edit mine".
        event.preventDefault();
        backToMine();
        return;
      }
      if (canEdit()) {
        // The full button edits its label; only Try it can run the sample form.
        const label = inline || target.closest('[data-popup-submit], .pe-popup-decline')?.querySelector('[data-popup-inline]');
        if (label) {
          event.preventDefault();
          if (doc.activeElement !== label) label.focus({ preventScroll: true });
          selectField(label.dataset.popupInline, 'preview');
          return;
        }
      }
      const button = target.closest('button, [data-popup-focus-field]');
      if (!button || !root.contains(button)) {
        if (canEdit() && mode === 'edit' && (target === card || target.matches('.pe-card-copy'))) selectField('background', 'preview');
        return;
      }
      if (button.dataset.popupInteraction) {
        if (stash) leaveMakeover();
        setInteraction(button.dataset.popupInteraction); preview();
      } else if (button.matches('[data-popup-edit-done]')) {
        doc.activeElement?.blur(); clearEditing();
      } else if (button.matches('[data-popup-review-start]')) {
        startReview();
        if (!homepage && view.innerWidth <= 650) {
          const rail = one('.pe-rail');
          rail?.focus({ preventScroll: true });
          revealInPage(rail, 'start');
        }
      } else if (button.dataset.popupMode === 'review') showChecklist();
      else if (button.dataset.popupMode === 'edit') { if (stash) leaveMakeover(); setMode('edit'); setInteraction('edit'); preview(); }
      else if (button.matches('[data-popup-reset]')) reset();
      else if (button.dataset.popupDevice) {
        const device = button.dataset.popupDevice === 'mobile' ? 'mobile' : 'desktop';
        if (canvas) canvas.dataset.device = device;
        root.dataset.device = device;
        all('[data-popup-device]').forEach(el => el.setAttribute('aria-pressed', String(el.dataset.popupDevice === device)));
        fitCard();
      } else if (button.dataset.popupPreviewMode) preview(button.dataset.popupPreviewMode === 'success' ? 'success' : 'form');
      else if (button.matches('[data-popup-submit]')) trySubmit();
      else if (button.matches('[data-popup-close]')) preview('closed');
      else if (button.matches('[data-popup-reopen]')) preview();
      else if (button.matches('[data-popup-view-preview]')) {
        const target = (mode === 'review' && reviewTargets[0]) || targetFor(root.dataset.editingField) || one('.pe-header');
        revealInPage(target, 'center');
        if (canEdit() && target.matches('[data-popup-inline]')) {
          const input = one(`[data-popup-field="${target.dataset.popupInline}"]`);
          target.focus({ preventScroll: true });
          const range = doc.createRange();
          range.setStart(...textPoint(target, input.selectionStart || 0));
          range.setEnd(...textPoint(target, input.selectionEnd || 0));
          const selection = view.getSelection(); selection.removeAllRanges(); selection.addRange(range);
        }
      }
      else if (button.matches('[data-popup-view-settings]')) {
        const input = one(`[data-popup-field="${root.dataset.editingField}"]`);
        if (input) { input.focus({ preventScroll: true }); revealInPage(input, 'center'); }
      }
      else if (button.matches('[data-popup-fix]')) applyFix(button.dataset.popupFix);
      else if (button.matches('[data-popup-check]')) selectCheck(button.dataset.popupCheck);
      else if (button.matches('[data-popup-edit-check]')) editFromCheck(button.dataset.popupEditCheck);
      else if (button.matches('[data-popup-edit-field]')) editField(button.dataset.popupEditField);
      else if (button.matches('[data-popup-locate]')) locate(button.dataset.popupLocate);
      else if (button.matches('[data-popup-back-to-checklist]')) {
        const from = followCheck;
        showChecklist();
        if (from) selectCheck(from);
      }
      else if (button.matches('[data-popup-undo-button]')) undo(button);
      else if (button.matches('[data-popup-focus-field]')) {
        if (!canEdit()) return;
        setMode('edit');
        const input = one(`[data-popup-field="${button.dataset.popupFocusField}"]`);
        if (input) {
          const group = input.closest('details');
          if (group) group.open = true;
          input.focus({ preventScroll: true });
          selectField(button.dataset.popupFocusField, 'preview');
          if (pageScroll || stacked()) revealInPage(input);
        }
      } else if (button.matches('[data-popup-copy]')) { void copyText(); }
      else if (button.matches('[data-popup-download-notes]')) downloadNotes();
      else if (button.matches('[data-popup-close-handoff]')) {
        show(one('[data-popup-handoff]'), false);
        all('[data-popup-download-notes][aria-controls]').forEach(el => el.setAttribute('aria-expanded', 'false'));
        one('.pe-example-actions [data-popup-download-notes]')?.focus({ preventScroll: true });
      } else if (button.matches('[data-popup-photo-reset]')) resetPhoto();
      else if (button.matches('[data-popup-makeover-start]')) requestMakeover();
      else if (button.matches('[data-popup-makeover-confirm]')) startMakeover();
      else if (button.matches('[data-popup-makeover-cancel]')) { confirming = false; renderReview(); focusReviewControl('[data-popup-makeover-start]'); }
      else if (button.matches('[data-makeover-keep]')) keepMakeover();
      else if (button.matches('[data-makeover-leave], [data-makeover-edit]')) backToMine();
    });
    listen(root, 'keydown', event => {
      const target = event.target;
      if (target.matches('[data-popup-inline]') && interaction === 'edit') {
        // Space and Enter on a button label must never trigger the parent button.
        event.stopPropagation();
        if (!event.isComposing && !composing && (event.key === 'Escape' || (event.key === 'Enter' && target.dataset.popupInline !== 'subheading'))) {
          event.preventDefault(); target.blur(); clearEditing();
        }
        return;
      }
      if (target.matches('[data-popup-focus-field]') && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault(); target.click(); return;
      }
      if (target.matches('[data-popup-try-field]') && event.key === 'Enter') {
        event.preventDefault(); trySubmit(); return;
      }
      if (!target.matches('[data-popup-check]') || !['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      const ids = report.checks.map(row => row.id), index = ids.indexOf(target.dataset.popupCheck);
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? ids.length - 1
        : (index + (event.key === 'ArrowUp' || event.key === 'ArrowLeft' ? -1 : 1) + ids.length) % ids.length;
      selectCheck(ids[next]);
    });
    listen(root, 'submit', event => { event.preventDefault(); });
    listen(view, 'pagehide', () => {
      // A page frozen mid-makeover comes back as the visitor's own pop-up.
      if (stash) { leaveMakeover(); setMode('edit'); setInteraction('edit'); preview(); root.dataset.reviewState = 'idle'; }
      if (downloadURL) { view.URL.revokeObjectURL(downloadURL); downloadURL = null; }
    });
    listen(view, 'pageshow', () => {
      if (destroyed || !storage || stash) return;
      // The other copy of this editor (homepage or standalone) may have changed
      // the shared draft while this page sat in the back-forward cache.
      try {
        const saved = storage.getItem(draftStorageKey);
        const draft = saved ? restoreDraft(JSON.parse(saved)) : null;
        if (draft && !sameDraft(draft, config)) {
          config = draft; dirty = true; clearChange();
          renderConfig(); preview();
          if (mode === 'review') renderReview();
        }
      } catch (_) { /* Keep the in-memory draft if storage is blocked or damaged. */ }
    });
    if (pageScroll && one('[data-popup-makeover-panel]') && view.MyUserPopupMakeover) {
      makeover = view.MyUserPopupMakeover.create({
        root, view, rules, paint: paintCard,
        write: draft => { config = { ...draft }; renderConfig(); preview(); }
      });
    }
    renderConfig(); preview(); setMode('edit');
    all('[data-popup-review-start]').forEach(button => { button.disabled = false; });
    if (storageBlocked) notice('This browser cannot save your draft. Download your notes before you leave.');
    else if (restored) notice('Your draft is back. Download your notes to keep it after this tab closes.');
    return {
      getState: () => ({ config: { ...config }, report: rules.evaluate(config), mode, selected, makeover: !!stash,
        change: lastChange ? lastChange.label : null }),
      startReview,
      destroy() {
        if (stash) leaveMakeover();
        destroyed = true;
        makeover?.destroy(); clearEditing(); listeners.forEach(remove => remove());
        if (downloadURL) view.URL.revokeObjectURL(downloadURL);
        if (photoURL) view.URL.revokeObjectURL(photoURL);
      }
    };
  }
  return { mount };
});
